import { useCallback, useEffect } from 'react'

import { CANARIAS_ID } from '@/constants'
import type { Address } from '@/models'

import { useUpdateTaxRegime } from '@/hooks/tax-regime'

import { useAppDispatch, useAppSelector } from '../store'
import { createUserAddress, requestUserAddresses, saveUserAddress } from './actions'
import addressesSelector from './selectors'

export const useUserAddresses = () => {
    const dispatch = useAppDispatch()

    const addresses = useAppSelector(addressesSelector)
    const { shouldFetch } = addresses

    useEffect(() => {
        if (!shouldFetch) return
        dispatch(requestUserAddresses())
    }, [dispatch, shouldFetch])

    return addresses
}

export const useCreateOrUpdateAddress = () => {
    const dispatch = useAppDispatch()

    const createOrUpdateAddress = useCallback(
        (address: Partial<Address>) =>
            address.id ? dispatch(saveUserAddress(address)) : dispatch(createUserAddress(address)),
        [dispatch]
    )

    return {
        createOrUpdateAddress,
    }
}

export const useSaveAddressAndTaxRegime = () => {
    const { updateTaxRegime } = useUpdateTaxRegime()
    const { createOrUpdateAddress } = useCreateOrUpdateAddress()

    const saveAddressAndTaxRegime = useCallback(
        (address: Partial<Address>) => {
            const isUpdatedAddressCanaryIslands = Boolean(address?.autonomousRegionId === CANARIAS_ID)
            return createOrUpdateAddress(address).then(() => updateTaxRegime(isUpdatedAddressCanaryIslands))
        },
        [createOrUpdateAddress, updateTaxRegime]
    )

    return {
        saveAddressAndTaxRegime,
    }
}
